import { addMinutes, format } from "date-fns";
import { VisuallyHidden } from "radix-ui";
import { t } from "ttag";

import { LogoLoader } from "@/shared/components";
import { formatTime } from "@/shared/lib";
import type { ModalProps } from "@/shared/models/legacy-types";
import { Button, Card, Dialog, StarsIcon } from "@/shared/ui";
import { useUnsubscribeToSession } from "../hooks";
import type { Session } from "../models";

type UnsubscribeSessionModalProps = ModalProps & {
  session: Session;
};

export const UnsubscribeSessionModal = ({
  show,
  onClose,
  session,
}: UnsubscribeSessionModalProps) => {
  const unsubscribeToSessionMutation = useUnsubscribeToSession();

  const handleUnsubscribe = () => {
    if (session) {
      unsubscribeToSessionMutation.mutate(session.id, {
        onSuccess: onClose,
      });
    }
  };

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>{t`Unsubscribe from this session?`}</Dialog.Title>

          <VisuallyHidden.Root asChild>
            <Dialog.Description>
              {t`By clicking the "Unsubscribe" button, you are missing your place for this session.`}
            </Dialog.Description>
          </VisuallyHidden.Root>
        </Dialog.Header>

        <Card.Root className="gap-1.5 p-8">
          <div className="absolute top-0 left-0 flex size-10 -translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full bg-primary-50">
            <StarsIcon className="size-4 text-primary-100" />
          </div>
          <Card.Title>{session.title}</Card.Title>
          <Card.Description className="text-base font-medium">
            {format(session.startAt, "MM/dd/yy")}
          </Card.Description>
          <Card.Description className="text-base text-neutral-900 lowercase">
            {`${formatTime(session.startAt)} - ${formatTime(addMinutes(session.startAt, session.duration))}`}
          </Card.Description>
        </Card.Root>

        <Dialog.Footer>
          <Button size="lg" onClick={handleUnsubscribe}>
            {t`Unsubscribe`}
          </Button>
        </Dialog.Footer>

        <LogoLoader show={unsubscribeToSessionMutation.isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
